import { useEffect, useState } from 'react';
import axios from 'axios';
import { Tag } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const TAG_LABELS = {
  dividend_safe: { label: 'Dividend Safe', emoji: '🛡️', tone: 'emerald' },
  yield_trap: { label: 'Yield Trap Risk', emoji: '⚠️', tone: 'rose' },
  dividend_growth: { label: 'Dividend Growth', emoji: '📈', tone: 'sky' },
  drip_favorite: { label: 'DRIP Favorite', emoji: '💧', tone: 'violet' },
  overvalued: { label: 'Overvalued', emoji: '💰', tone: 'amber' },
  undervalued: { label: 'Undervalued', emoji: '🎯', tone: 'emerald' },
  bullish_long_term: { label: 'Bullish Long-Term', emoji: '🐂', tone: 'sky' },
  income_snowball: { label: 'Income Snowball', emoji: '❄️', tone: 'violet' },
};

const TONE_CLASSES = {
  emerald: {
    on: 'border-emerald-500 bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-200',
    off: 'border-border bg-muted/30 text-muted-foreground hover:bg-emerald-50 dark:hover:bg-emerald-950/30 hover:text-emerald-700 dark:hover:text-emerald-300',
  },
  rose: {
    on: 'border-rose-500 bg-rose-100 dark:bg-rose-900/40 text-rose-800 dark:text-rose-200',
    off: 'border-border bg-muted/30 text-muted-foreground hover:bg-rose-50 dark:hover:bg-rose-950/30 hover:text-rose-700 dark:hover:text-rose-300',
  },
  sky: {
    on: 'border-sky-500 bg-sky-100 dark:bg-sky-900/40 text-sky-800 dark:text-sky-200',
    off: 'border-border bg-muted/30 text-muted-foreground hover:bg-sky-50 dark:hover:bg-sky-950/30 hover:text-sky-700 dark:hover:text-sky-300',
  },
  violet: {
    on: 'border-violet-500 bg-violet-100 dark:bg-violet-900/40 text-violet-800 dark:text-violet-200',
    off: 'border-border bg-muted/30 text-muted-foreground hover:bg-violet-50 dark:hover:bg-violet-950/30 hover:text-violet-700 dark:hover:text-violet-300',
  },
  amber: {
    on: 'border-amber-500 bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200',
    off: 'border-border bg-muted/30 text-muted-foreground hover:bg-amber-50 dark:hover:bg-amber-950/30 hover:text-amber-700 dark:hover:text-amber-300',
  },
};

/**
 * SentimentTagPicker — Round 84k (G).
 *
 * Renders structured sentiment tag chips below a community post.
 * Anyone (including the author) can toggle a tag — counts aggregate
 * across all users. Used by viewers to register quick categorical
 * agreement with the post's framing.
 *
 * Powered by:
 *   GET /api/community/posts/{post_id}/tags
 *   POST /api/community/posts/{post_id}/tags/{tag}  (toggle)
 */
export default function SentimentTagPicker({ postId, compact = false, initialCounts = null, initialMyTags = null }) {
  const [counts, setCounts] = useState(initialCounts || {});
  const [myTags, setMyTags] = useState(new Set(initialMyTags || []));
  const [pending, setPending] = useState(null);
  const [loaded, setLoaded] = useState(!!initialCounts);
  const [hovering, setHovering] = useState(false);

  // Lazy-load tag counts the first time the picker enters the viewport
  // (or via hover for compact mode) so we don't pile N requests on the
  // feed for posts the user never interacts with.
  useEffect(() => {
    if (loaded) return;
    if (compact && !hovering) return;
    let cancelled = false;
    axios.get(`${API}/community/posts/${postId}/tags`, { withCredentials: true })
      .then((res) => {
        if (cancelled) return;
        setCounts(res.data?.tag_counts || {});
        setMyTags(new Set(res.data?.my_tags || []));
        setLoaded(true);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [postId, hovering, loaded, compact]);

  const toggle = async (tag) => {
    if (pending) return;
    setPending(tag);
    try {
      const { data } = await axios.post(
        `${API}/community/posts/${postId}/tags/${tag}`,
        {},
        { withCredentials: true },
      );
      setCounts(data?.tag_counts || {});
      setMyTags(new Set(data?.my_tags || []));
    } catch { /* silent — likely 401 (anon) */ }
    finally { setPending(null); }
  };

  const allTags = Object.keys(TAG_LABELS);
  // In compact mode (post feed), only show tags with ≥1 vote unless the
  // user is hovering. In picker mode (post detail / composer), show all.
  const visible = compact && !hovering
    ? allTags.filter((t) => (counts[t] || 0) > 0)
    : allTags;

  if (compact && visible.length === 0 && !hovering) {
    return (
      <button
        type="button"
        onMouseEnter={() => setHovering(true)}
        onClick={() => setHovering(true)}
        className="inline-flex items-center gap-1 text-[10px] text-muted-foreground hover:text-foreground transition-colors"
        data-testid={`sentiment-tag-add-${postId}`}
      >
        <Tag className="w-3 h-3" />
        Tag this
      </button>
    );
  }

  return (
    <div
      className="flex flex-wrap gap-1.5"
      onMouseLeave={() => setHovering(false)}
      data-testid={`sentiment-tag-picker-${postId}`}
    >
      {visible.map((tag) => {
        const meta = TAG_LABELS[tag];
        const isOn = myTags.has(tag);
        const count = counts[tag] || 0;
        const classes = TONE_CLASSES[meta.tone];
        return (
          <button
            key={tag}
            type="button"
            disabled={pending === tag}
            onClick={() => toggle(tag)}
            className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full border transition-colors disabled:opacity-50 ${
              isOn ? classes.on : classes.off
            }`}
            data-testid={`sentiment-tag-${tag}-${postId}`}
            title={isOn ? `Click to remove your ${meta.label} vote` : `Tag this post as ${meta.label}`}
          >
            <span>{meta.emoji}</span>
            <span>{meta.label}</span>
            {count > 0 && (
              <span className="ml-0.5 px-1 rounded-full bg-background/60 text-foreground/80 font-mono">{count}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}
